import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react"
import { format } from "date-fns"
import { ArrowLeft } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Badge, Button, Textarea } from "@f2e/ui/components/base"

import { EmptyDataPanel, LoadingPage } from "@/components/System"
import { useToast } from "@/hooks"
import {
  FetchStatus,
  useGetWorkspacePaymentRequestApi,
  useUpdateWorkspacePaymentRequestApi,
} from "@/hooks/api"
import { Workspace } from "@/types"

export interface WorkspacePaymentRequestDetailProps {
  workspace: Workspace
  paymentRequestId: string
  onBack: () => void
}

const formatDateTime = (value?: string | null) => {
  if (!value) return "—"
  return format(new Date(value), "yyyy-MM-dd HH:mm")
}

const getStatusBadgeClassName = (status: string) => {
  switch (status) {
    case "succeeded":
      return "bg-green-600 text-white hover:bg-green-700 border-transparent"
    case "pending":
      return "bg-slate-900 text-white hover:bg-slate-800 border-transparent"
    case "processing":
      return "bg-amber-500 text-white hover:bg-amber-600 border-transparent"
    case "failed":
      return "bg-rose-600 text-white hover:bg-rose-700 border-transparent"
    case "refunded":
      return "bg-violet-600 text-white hover:bg-violet-700 border-transparent"
    case "cancelled":
    case "expired":
      return "bg-slate-200 text-slate-700 hover:bg-slate-200 border-transparent dark:bg-slate-800 dark:text-slate-200"
    default:
      return ""
  }
}

const renderValue = (value?: string | number | null) => {
  if (value === null || value === undefined || value === "") return "—"
  return String(value)
}

const DetailField = ({ label, value }: { label: string; value: ReactNode }) => (
  <div className="space-y-1">
    <p className="text-sm font-medium text-muted-foreground">{label}</p>
    <div className="break-words text-sm leading-6 text-foreground">{value}</div>
  </div>
)

const WorkspacePaymentRequestDetail = ({
  workspace,
  paymentRequestId,
  onBack,
}: WorkspacePaymentRequestDetailProps) => {
  const { t } = useTranslation()
  const { alertToast, successToast } = useToast()
  const [adminNote, setAdminNote] = useState("")

  const {
    data: detailResp,
    error: detailError,
    fetchFn: fetchPaymentRequest,
    status: detailStatus,
  } = useGetWorkspacePaymentRequestApi(workspace.id, paymentRequestId)
  const {
    data: updateResp,
    error: updateError,
    fetchFn: updatePaymentRequest,
    status: updateStatus,
  } = useUpdateWorkspacePaymentRequestApi(workspace.id, paymentRequestId)

  useEffect(() => {
    fetchPaymentRequest()
  }, [fetchPaymentRequest])

  useEffect(() => {
    if (!detailResp?.data) return
    setAdminNote(detailResp.data.admin_note || "")
  }, [detailResp])

  useEffect(() => {
    if (!updateResp?.data) return
    fetchPaymentRequest({ preserveData: true })
    successToast({
      title: t("workspace.payments.detail.noteSavedTitle"),
      description: t("workspace.payments.detail.noteSavedDescription"),
    })
  }, [fetchPaymentRequest, updateResp, successToast, t])

  useEffect(() => {
    if (!updateError) return
    alertToast({
      title: t("workspace.payments.detail.errors.saveFailedTitle"),
      description: t("workspace.payments.detail.errors.saveFailedDescription"),
    })
  }, [updateError, alertToast, t])

  const paymentRequest = detailResp?.data
  const isNoteUnchanged = useMemo(() => {
    return (paymentRequest?.admin_note || "") === adminNote
  }, [adminNote, paymentRequest?.admin_note])

  const handleSave = useCallback(() => {
    updatePaymentRequest({
      jsonBody: {
        admin_note: adminNote,
      },
    })
  }, [adminNote, updatePaymentRequest])

  if (detailStatus === FetchStatus.Loading && !paymentRequest) {
    return <LoadingPage message={t("workspace.payments.detail.loading")} />
  }

  if (detailError || !paymentRequest) {
    return (
      <section className="space-y-4">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={onBack}
            data-testid="workspace-payment-request-back"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="space-y-1">
            <h4 className="text-lg font-semibold">
              {t("workspace.payments.detail.title")}
            </h4>
          </div>
        </div>
        <EmptyDataPanel
          message={t("workspace.payments.detail.errors.loadFailed")}
        />
      </section>
    )
  }

  return (
    <section className="space-y-4">
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={onBack}
            data-testid="workspace-payment-request-back"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="space-y-1">
            <h4 className="text-lg font-semibold">
              {t("workspace.payments.detail.title")}
            </h4>
          </div>
        </div>
        <div className="space-y-1">
          <h4 className="text-lg font-semibold">
            {renderValue(paymentRequest.item_desc)}
          </h4>
          <p className="text-sm text-muted-foreground">
            {t("workspace.payments.detail.description")}
          </p>
        </div>
      </div>

      <div className="rounded-lg border p-4">
        <div className="mb-4 space-y-1">
          <h5 className="font-medium">
            {t("workspace.payments.detail.sections.metadata")}
          </h5>
          <p className="text-sm text-muted-foreground">
            {t("workspace.payments.detail.metadataHint")}
          </p>
        </div>

        <div className="grid gap-x-8 gap-y-5 md:grid-cols-2 xl:grid-cols-3">
          <DetailField
            label={t("workspace.payments.requestList.table.requestNumber")}
            value={paymentRequest.merchant_order_no}
          />
          <DetailField
            label={t("workspace.payments.requestList.table.title")}
            value={renderValue(paymentRequest.item_desc)}
          />
          <DetailField
            label={t("workspace.payments.detail.fields.status")}
            value={
              <Badge
                variant="secondary"
                className={getStatusBadgeClassName(paymentRequest.status)}
              >
                {t(
                  `workspace.payments.requestList.statuses.${paymentRequest.status}`,
                  paymentRequest.status
                )}
              </Badge>
            }
          />
          <DetailField
            label={t("workspace.payments.detail.fields.amount")}
            value={String(paymentRequest.amount_twd)}
          />
          <DetailField
            label={t("workspace.payments.detail.fields.agent")}
            value={renderValue(paymentRequest.agent_name)}
          />
          <DetailField
            label={t("workspace.payments.detail.fields.externalUserId")}
            value={renderValue(paymentRequest.external_user_id)}
          />
          <DetailField
            label={t("workspace.payments.detail.fields.paymentMethod")}
            value={renderValue(paymentRequest.payment_method)}
          />
          <DetailField
            label={t("workspace.payments.detail.fields.tradeNo")}
            value={renderValue(paymentRequest.trade_no)}
          />
          <DetailField
            label={t("workspace.payments.detail.fields.paidAt")}
            value={formatDateTime(paymentRequest.paid_at)}
          />
          <DetailField
            label={t("workspace.payments.detail.fields.createdAt")}
            value={formatDateTime(paymentRequest.created_at)}
          />
          <DetailField
            label={t("workspace.payments.detail.fields.updatedAt")}
            value={formatDateTime(paymentRequest.updated_at)}
          />
        </div>
      </div>

      <div className="rounded-lg border p-4">
        <div className="space-y-3">
          <div className="space-y-1">
            <h5 className="font-medium">
              {t("workspace.payments.detail.internalNote.title")}
            </h5>
            <p className="text-sm text-muted-foreground">
              {t("workspace.payments.detail.internalNote.description")}
            </p>
          </div>

          <Textarea
            value={adminNote}
            onChange={(event) => setAdminNote(event.target.value)}
            rows={7}
            className="resize-y"
            placeholder={t(
              "workspace.payments.detail.internalNote.placeholder"
            )}
          />

          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-muted-foreground">
              {t("workspace.payments.detail.internalNote.helperText")}
            </p>
            <Button
              onClick={handleSave}
              disabled={updateStatus === FetchStatus.Loading || isNoteUnchanged}
            >
              {updateStatus === FetchStatus.Loading
                ? t("workspace.payments.detail.saving")
                : t("workspace.payments.detail.save")}
            </Button>
          </div>
        </div>
      </div>
    </section>
  )
}

export default WorkspacePaymentRequestDetail
